import * as GC from '@grapecity/spread-sheets';
import { copyTextToClipboard } from '@shared/copy';
import { colours, DASH } from './spread-settings';

const BREAK_MARGIN = 1;

const interval: NodeJS.Timeout = null;

export const removeSheetByName = (workbook: GC.Spread.Sheets.Workbook, sheetName = 'Sheet1') => {
  if (workbook.getSheetFromName(sheetName)) {
    const sheetIndex = workbook.getSheetIndex(sheetName);
    workbook.removeSheet(sheetIndex);
  }
};

export const addSheet = (workbook: GC.Spread.Sheets.Workbook, sheet: GC.Spread.Sheets.Worksheet, sheetIndex: number = -1): number => {
  sheetIndex = sheetIndex > -1 ? sheetIndex : workbook.getSheetCount() + 1;
  const alreadyExists = !!(sheet && workbook.getSheetFromName(sheet.name()));

  if (!alreadyExists) {
    try {
      workbook.addSheet(sheetIndex, sheet);
    } catch (err) {
      console.error(`SpreadJS Error. Unable to add sheet`, { sheetIndex, sheet, workbook });
    }
  } else {
    console.error(`Unable to add sheet`, { sheet, workbook, sheetIndex });
  }

  return workbook.getSheetCount();
};

export const addRowBreakToSheet = (sheet: GC.Spread.Sheets.Worksheet, rowIndex: number): number => {
  if (sheet) {
    rowIndex += BREAK_MARGIN - 1;
    const fullWideRange = sheet.getRange(rowIndex, 0, 1, sheet.getRowCount());
    fullWideRange.setBorder(new GC.Spread.Sheets.LineBorder(colours.BLACK, GC.Spread.Sheets.LineStyle.double), { bottom: true });
    rowIndex += BREAK_MARGIN;
    return rowIndex;
  }
};

export const addColumnBreakToSheet = (sheet: GC.Spread.Sheets.Worksheet, colIndex: number): number => {
  if (sheet) {
    colIndex += BREAK_MARGIN - 1;
    const fullWideRange = sheet.getRange(colIndex, 0, 1, sheet.getRowCount());
    fullWideRange.setBorder(new GC.Spread.Sheets.LineBorder(colours.BLACK, GC.Spread.Sheets.LineStyle.double), { left: true });
    colIndex += BREAK_MARGIN;
    return colIndex;
  }
};

export const getColumnHeaderName = ({ sheet, col }: { sheet: GC.Spread.Sheets.Worksheet; col: number }) =>
  sheet && col > -1 && sheet.getValue(0, col, GC.Spread.Sheets.SheetArea.colHeader);

export function getColumnNames(sheet: GC.Spread.Sheets.Worksheet): string[] {
  if (sheet) {
    const columnNames = [];
    for (let i = 0; i < sheet.getColumnCount(); i++) {
      const colName = sheet.getDataColumnName(i);
      columnNames.push(colName);
    }
    return columnNames;
  }
}

export function allowPastingRowsBeyond(sheet: GC.Spread.Sheets.Worksheet, info) {
  if (sheet) {
    const numRows = sheet.getRowCount();
    const pastingExcess = info.cellRange.row + info.cellRange.rowCount - numRows;
    sheet.addRows(sheet.getRowCount() - 1, pastingExcess);
  }
}

// Locks the columns beyond the current columns count of the sheet
export function preventPastingColumnsBeyond(sheet: GC.Spread.Sheets.Worksheet, event, info) {
  if (sheet) {
    // Prevent pasting from exceed the max column count
    const maxColumns = getColumnNames(sheet).length;
    const pastingExcess = info.cellRange.col + info.cellRange.colCount - maxColumns;

    if (pastingExcess > 0) {
      // console.log(`[Pasting prevented] The information trying to paste is ${pastingExcess} columns greater than allowed`);
      event.preventDefault();
      return;
    }
  }
}

export const setWatermark = (range: GC.Spread.Sheets.CellRange, txt: string, alignRight = false) => {
  return range
    .watermark(txt)
    .hAlign(alignRight ? GC.Spread.Sheets.HorizontalAlign.right : GC.Spread.Sheets.HorizontalAlign.left)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center);
};

/**
 * @param [startCol] - Column number to start auto-fitting (default: 0)
 * @param [endCol] - Column number (inclusive) to end auto-fitting (default: null (last))
 * @param [minColWidth] - Minimum column width (default: 140)
 * @param [maxColWidth] - Maximum column width (default: 200). Set to NULL for no maximum.
 * @param [noExtraPixels] - List of columns which won´t receive 4 extra pixels to avoid text-wrap when column is selected.
 */
export const autofitColumns = (
  sheet: GC.Spread.Sheets.Worksheet,
  startCol = 0,
  endCol: number = null,
  minColWidth = 140,
  maxColWidth = 200,
  noExtraPixels: number[] = null
) => {
  try {
    if (endCol === null) {
      endCol = sheet.getColumnCount();
    }

    for (let i = startCol; i <= endCol; i++) {
      sheet.autoFitColumn(i);
      if (sheet.getColumnWidth(i) < minColWidth) {
        sheet.setColumnWidth(i, minColWidth);
      } else if (maxColWidth !== null && sheet.getColumnWidth(i) > maxColWidth) {
        sheet.setColumnWidth(i, maxColWidth);
      } else {
        const currentWidth = sheet.getColumnWidth(i);
        if (noExtraPixels !== null && !noExtraPixels.includes(i)) {
          // Extra width pixels so header don't wrap when column is selected
          sheet.setColumnWidth(i, currentWidth + 4);
        }
      }
    }
  } catch (error) {}
};

export const autofitRows = (
  sheet: GC.Spread.Sheets.Worksheet,
  startRow = 0,
  endRow: number = null,
  minRowHeight = 20,
  maxRowHeight = 500
) => {
  if (sheet) {
    if (endRow === null) {
      endRow = sheet.getRowCount();
    }

    for (let i = startRow; i <= endRow; i++) {
      sheet.autoFitRow(i);
      if (sheet.getRowHeight(i) < minRowHeight) {
        sheet.setRowHeight(i, minRowHeight);
      } else if (sheet.getRowHeight(i) > maxRowHeight) {
        sheet.setRowHeight(i, maxRowHeight);
      }
    }
  }
};

export const copyValues = (context: GC.Spread.Sheets.Workbook, options, isUndo) => {
  let text = '';
  const sheet: GC.Spread.Sheets.Worksheet = context.getActiveSheet();
  const selection = sheet.getSelections()[0];
  if (selection.row === -1) {
    // Header area selected
    selection.row = 0;
  }

  if (selection.col === -1) {
    selection.col = 0;
  }

  const data = sheet.getArray(selection.row, selection.col, selection.rowCount, selection.colCount);
  data.forEach((entireRow, rowIndex) => {
    entireRow.forEach((col, colIndex) => {
      if (col === undefined || col === null) {
        col = '';
      }
      text += col + (colIndex + 1 < selection.colCount ? '\t' : '');
    });
    if (rowIndex + 1 < selection.rowCount) {
      text += '\n';
    }
  });
  return copyTextToClipboard(text);
};

export const setBlankCellValue = (cell: GC.Spread.Sheets.CellRange) => {
  cell
    .value(DASH)
    // Styling
    .wordWrap(true)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center);
};

export const flashRange = (sheet: GC.Spread.Sheets.Worksheet, cellsToFlash: { cell: GC.Spread.Sheets.CellRange; value: any }[]) => {
  try {
    sheet.suspendPaint();

    const flashInterval = 250;
    const lastIndex = 3;
    const originalColor = cellsToFlash[0].cell.backColor();

    for (let index = 0; index <= lastIndex; index++) {
      setTimeout(() => {
        const colour = index % 2 === 0 ? colours.FLASH : originalColor;

        cellsToFlash.forEach(c => {
          c.cell.backColor(colour);
        });
        sheet.resumePaint();
        sheet.suspendPaint();

        if (index === lastIndex) {
          cellsToFlash.forEach(c => {
            c.cell.value(c.value);
          });

          sheet.resumePaint();
        }
      }, index * flashInterval);
    }
  } catch (error) {
    clearInterval(interval);
    console.warn(error);
  }
};

export const isEmptyRow = (sheet: GC.Spread.Sheets.Worksheet, row: number): boolean => {
  const entireRow = sheet.getArray(row, 0, 1, sheet.getColumnCount());
  return entireRow[0] ? entireRow[0].filter(x => x !== null && x !== '').length === 0 : null;
};

/**
 * Trim empty bottom rows, and keeps `paddingRows` bottom empty rows
 */
export const setRowsAndColumns = (sheet: GC.Spread.Sheets.Worksheet, paddingRows: number = 3) => {
  const sheetRows = sheet.getArray(0, 0, sheet.getRowCount(), sheet.getColumnCount());
  const bools = sheetRows.map(r => r.some(Boolean));
  const lastNonEmptyIndex = bools.reverse().findIndex(r => r);
  const rowCount = bools.length - lastNonEmptyIndex + paddingRows;
  sheet.setRowCount(rowCount);
};
